import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { LineChart } from '@client/app/modules/charts/models/charts/line';
import { ChartsDataHelper } from '@client/app/blocks/helpers/charts/charts-data-helper';
import MonColors from '@monsido/colors/dist/es6/colors';
import { LegendCursorService } from '@client/app/modules/charts/services/LegendCursor.service';
import {
    PagePerformanceVersionService,
    VersionInfoType,
} from '@client/app/modules/page-performance/services/page-performance-version/page-performance-version.service';
import { MonNumberService } from '@monsido/core/locale/services/mon-number.service';
import { ChartDataEntry, ChartOptions, ChartVerticalLines } from '@client/app/blocks/service/charts/base-chart/chart-factory';

interface PerformanceHistorySeries {
    label: string;
    key: string;
    percentageValue: boolean;
    yAxis: string;
    style: {
        fill: boolean;
        borderDash: number[];
        color: string;
    };
    unit: string;
}

@Injectable({
    providedIn: 'root',
})
export class PerformanceDomainHistoryLineChartService {
    private readonly PERFORMANCE_HISTORY_SERIES: { [key: string]: PerformanceHistorySeries } = {
        AVERAGE_PERFORMANCE_SCORE: {
            label: this.translateService.getString('Average Performance Score'),
            key: 'performance_score_average',
            percentageValue: true,
            yAxis: 'y-axis-0',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['secondary-9'],
            },
            unit: '',
        },
        AVERAGE_FMP: {
            label: this.translateService.getString('Average First Meaningful Paint'),
            key: 'performance_first_meaningful_paint_average',
            percentageValue: false,
            yAxis: 'y-axis-1',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['secondary-17'],
            },
            unit: 'ms',
        },
        AVERAGE_FCP: {
            label: this.translateService.getString('Average First Contentful Paint'),
            key: 'performance_first_contentful_paint_average',
            percentageValue: false,
            yAxis: 'y-axis-1',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['secondary-13'],
            },
            unit: 'ms',
        },
        AVERAGE_LCP: {
            label: this.translateService.getString('Average Largest Contentful Paint'),
            key: 'performance_largest_contentful_paint_average',
            percentageValue: false,
            yAxis: 'y-axis-1',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['secondary-12'],
            },
            unit: 'ms',
        },
        AVERAGE_TTI: {
            label: this.translateService.getString('Average Time to Interactive'),
            key: 'performance_time_to_interactive_average',
            percentageValue: false,
            yAxis: 'y-axis-1',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['branding-1'],
            },
            unit: 'ms',
        },
        AVERAGE_SI: {
            label: this.translateService.getString('Average Speed Index'),
            key: 'performance_speed_index_average',
            percentageValue: false,
            yAxis: 'y-axis-1',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['secondary-16'],
            },
            unit: 'ms',
        },
        AVERAGE_TBT: {
            label: this.translateService.getString('Average Total Blocking Time'),
            key: 'performance_total_blocking_time_average',
            percentageValue: false,
            yAxis: 'y-axis-1',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['branding-4'],
            },
            unit: 'ms',
        },
        AVERAGE_CLS: {
            label: this.translateService.getString('Average Cumulative Layout Shift'),
            key: 'performance_cumulative_layout_shift_average',
            percentageValue: false,
            yAxis: 'y-axis-3',
            style: {
                fill: false,
                borderDash: [],
                color: MonColors['secondary-11'],
            },
            unit: 'decimals',
        },
        PERFORMANCE_PAGES: {
            label: this.translateService.getString('Performance Pages'),
            key: 'performance_page_count',
            percentageValue: false,
            yAxis: 'y-axis-2',
            style: {
                fill: true,
                borderDash: [5, 5],
                color: '#ff9d4e',
            },
            unit: 'pages',
        },
    };

    private readonly PERFORMANCE_HISTORY_SERIES_KEY_METRIC_MAP: { [key: string]: string } = {
        performance_first_contentful_paint_average: 'AVERAGE_FCP',
        performance_first_meaningful_paint_average: 'AVERAGE_FMP',
        performance_largest_contentful_paint_average: 'AVERAGE_LCP',
        performance_time_to_interactive_average: 'AVERAGE_TTI',
        performance_speed_index_average: 'AVERAGE_SI',
        performance_total_blocking_time_average: 'AVERAGE_TBT',
        performance_cumulative_layout_shift_average: 'AVERAGE_CLS',
    };

    private lineChartSettings!: LineChart;
    private metrics: any[] = [];
    private versions: (VersionInfoType | null)[] = [];
    private seriesByVersion: { [key: string]: any } = {};
    private metricsIds: { [key: string]: any } = {};
    private series: string[] = [];

    constructor (
        private translateService: TranslateService,
        private dateTimeService: DateTimeService,
        private pagePerformanceVersionService: PagePerformanceVersionService,
        private legendCursorService: LegendCursorService,
        private monNumberService: MonNumberService,
    ) {}

    createPerformanceDomainHistoryLineChart (): any {
        this.lineChartSettings = new LineChart();
        this.metrics = [];
        this.versions = [];
        this.seriesByVersion = {};
        this.metricsIds = {};
        this.series = [];

        return {
            getOption: this.getOption.bind(this),
            getColors: this.getColors.bind(this),
            getSeries: this.getSeries.bind(this),
            getOverride: this.getOverride.bind(this),
            getLabels: this.getLabels.bind(this),
            getData: this.getData.bind(this),
        };
    }

    getOption (history: any[]): ChartOptions {
        this.lineChartSettings.setTooltipSettings({
            intersect: false,
            mode: 'x-axis',
            callbacks: {
                label: (tooltipItem: any, data: any) => {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    const series = this.PERFORMANCE_HISTORY_SERIES[this.series[tooltipItem.datasetIndex]];
                    const scope = {
                        label: dataset.label,
                        value: this.monNumberService.transformNumber(tooltipItem.yLabel, '0,0'),
                    };

                    if (series.unit === 'ms') {
                        return this.translateService.getString('[[label]]: [[value]]ms', scope);
                    } else if (series.unit === 'pages') {
                        return this.translateService.getString('[[label]]: [[value]] Pages', scope);
                    } else if (series.unit === 'decimals') {
                        return this.translateService.getString('[[label]]: [[value]]', scope);
                    } else {
                        return this.translateService.getString('[[label]]: [[value]]', scope);
                    }
                },
                title: (tooltipItem: any[]) => {
                    return this.dateTimeService.format(tooltipItem[0].xLabel, 'MMM DD');
                },
            },
        });

        this.lineChartSettings.setLegendSettingsOverride({
            display: true,
            position: 'bottom',
            labels: {
                fontSize: 11,
                boxWidth: 10,
            },
        });

        this.lineChartSettings.setScalesSettings({
            yAxes: [
                {
                    id: 'y-axis-0',
                    type: 'linear',
                    position: 'left',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        max: 100,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: number) => {
                            return this.monNumberService.transformNumber(value, '0,0');
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Score'),
                        fontSize: 11,
                    },
                },
                {
                    id: 'y-axis-1',
                    type: 'linear',
                    position: 'right',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: number) => {
                            return this.monNumberService.transformNumber(value, '0,0');
                        },
                    },
                    gridLines: {
                        display: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Milliseconds'),
                        fontSize: 11,
                    },
                },
                {
                    id: 'y-axis-2',
                    display: false,
                    type: 'linear',
                    position: 'left',
                    ticks: {
                        display: false,
                        fontSize: 11,
                        min: 0,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: number) => {
                            return this.monNumberService.transformNumber(value, '0,0');
                        },
                    },
                    gridLines: {
                        display: false,
                    },
                    scaleLabel: {
                        display: false,
                    },
                },
                {
                    id: 'y-axis-3',
                    display: false,
                    type: 'linear',
                    position: 'left',
                    ticks: {
                        display: false,
                        fontSize: 11,
                        min: 0,
                        max: 1,
                        beginAtZero: true,
                    },
                    gridLines: {
                        display: false,
                    },
                    scaleLabel: {
                        display: false,
                    },
                },
            ],
            xAxes: [
                {
                    id: 'x-axis-0',
                    type: 'time',
                    gridLines: {
                        offsetGridLines: true,
                        drawBorder: false,
                        display: false,
                    },
                    ticks: {
                        fontSize: 11,
                        maxTicksLimit: 30,
                    },
                    time: {
                        minUnit: 'day',
                        displayFormats: {
                            millisecond: 'MMM D',
                            second: 'MMM D',
                            minute: 'MMM D',
                            hour: 'MMM D',
                            day: 'MMM D',
                            week: 'MMM D',
                            month: 'MMM D',
                            quarter: 'MMM D',
                            year: 'MMM D',
                        },
                    },
                },
            ],
        });

        const entriesWithVersionChanged = this.pagePerformanceVersionService.getEntriesWithVersionChanged(history, 'performance_version');

        if (entriesWithVersionChanged.length > 0) {
            const lines: ChartVerticalLines[] = [];
            entriesWithVersionChanged.forEach((entry: any, index: number) => {
                const previousEntryWithVersionChanged = entriesWithVersionChanged[index - 1];
                if (previousEntryWithVersionChanged) {
                    lines.push({
                        xAxisID: 'x-axis-0',
                        x: entry.date,
                        style: {
                            lineDash: [5, 5],
                            color: MonColors['secondary-15'],
                            width: 3,
                        },
                        label: {
                            text: entry.version,
                            color: MonColors['secondary-15'],
                            position: 'top',
                        },
                    });
                }
            });

            this.lineChartSettings.options.verticalLines = {
                lines,
                timeScale: history.map(entry => entry.date),
            };
        }

        const options = this.lineChartSettings.options;
        this.lineChartSettings.options = this.legendCursorService.setHoverState(options);
        return this.lineChartSettings.options;
    }

    getColors (history: any[]): string[] {
        this.setSeriesData(history);
        const colors = this.series.map(key => this.PERFORMANCE_HISTORY_SERIES[key].style.color);
        this.lineChartSettings.addColors(colors);
        return this.lineChartSettings.colors;
    }

    getSeries (crawls: any[]): string[] {
        this.setSeriesData(crawls);
        const labels = this.series.map(series => this.PERFORMANCE_HISTORY_SERIES[series].label);
        this.lineChartSettings.addSeries(labels);
        return this.lineChartSettings.series;
    }

    getOverride (crawls: any[]): any[] {
        this.setSeriesData(crawls);
        const overrides = this.series.map(key => {
            const series = this.PERFORMANCE_HISTORY_SERIES[key];
            return {
                xAxisID: 'x-axis-0',
                yAxisID: series.yAxis,
                type: 'line',
                fill: series.style.fill,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                pointHitRadius: 34,
                borderDash: series.style.borderDash,
            };
        });
        this.lineChartSettings.addOverrides(overrides);
        return this.lineChartSettings.override;
    }

    getLabels (crawls: any[] = []): number[] {
        this.setSeriesData(crawls);
        return crawls.map(crawl => new Date(crawl.date).getTime());
    }

    getData (crawls: any[]): any[][] {
        if (!Array.isArray(crawls) || crawls.length === 0) {
            return [];
        }
        this.setSeriesData(crawls);
        const helper = new ChartsDataHelper('time');

        return this.series.map(key => {
            const series = this.PERFORMANCE_HISTORY_SERIES[key];
            return crawls.map(crawl => {
                if (series.percentageValue) {
                    return this.getScoreValue(helper.setData(crawl, series.key));
                } else {
                    return helper.setData(crawl, series.key);
                }
            });
        });
    }

    private getScoreValue (data: ChartDataEntry): ChartDataEntry {
        if (typeof data === 'number') {
            return {
                x: new Date().toISOString(), // Default to current date if no x value
                y: data * 100,
            };
        }
        return {
            ...data,
            y: data.y * 100,
        };
    }

    private setSeriesData (history: any[]): void {
        if (history.length === 0) {
            return;
        }

        const seriesByVersion = this.pagePerformanceVersionService.getSeriesByVersion([...history], 'performance_version');
        this.versions = seriesByVersion.versions;
        this.seriesByVersion = seriesByVersion.series;

        if (this.versions.length === 1 && this.versions[0]) {
            this.metrics = this.versions[0].avgMetrics;
        } else {
            this.metrics = [];
            this.metricsIds = {};
            for (let i = 0; i < this.versions.length; i++) {
                const versionMetrics = this.versions[i]?.avgMetrics;
                if (versionMetrics) {
                    for (let j = 0; j < versionMetrics.length; j++) {
                        const versionMetric = versionMetrics[j];
                        if (!this.metricsIds[versionMetric.id]) {
                            this.metrics.push(versionMetric);
                            this.metricsIds[versionMetric.id] = versionMetric;
                        }
                    }
                }
            }
        }

        this.series = ['AVERAGE_PERFORMANCE_SCORE'];
        this.metrics.forEach(metric => {
            this.series.push(this.PERFORMANCE_HISTORY_SERIES_KEY_METRIC_MAP[metric.id]);
        });
        this.series.push('PERFORMANCE_PAGES');
    }
}
